import type {
  ActivityEntry,
  AssigneeLogEntry,
  ChatMessage,
  DeliverableEntry,
  EstimationMode,
  PimMode,
} from '@/lib/dd-queue/types'
import type { LinePart } from '@/lib/dd-queue/lineParts'
import type { SendBackCause } from '@/lib/dd-queue/sendBackCause'

/** Design file — session or server-backed (DD-BE-PR2b-FILES). */
export interface DesignFile {
  id: string
  name: string
  type: string
  size: number
  /** Local just-uploaded preview only; omit for persisted rows. */
  dataUrl?: string
  downloadUrl?: string
  uploadedBy: string
  t: string
}

/** Working / committed proof artifact — mirrors queue-store ProofVersion (demo + 001e). */
export interface ProofSupportingFile {
  id: string
  name: string
  type: string
  size: number
  /** Local just-uploaded preview only; omit for persisted rows. */
  dataUrl?: string
  downloadUrl?: string
}

export interface ProofVersion {
  id: string
  version: number
  name: string
  type: string
  size: number
  dataUrl?: string
  downloadUrl?: string
  /** DD-PROOF-THUMB-001 — derived JPEG thumb (never the original). */
  thumbDownloadUrl?: string
  uploadedBy: string
  t: string
  deletedAt?: string
  deletedBy?: string
  source?: 'design' | 'estimate'
  supportingFiles?: ProofSupportingFile[]
  /** Frozen at Design complete mint — from API packet_snapshot. */
  packetSnapshot?: Record<string, unknown>
}

/** Session overlay keyed `${requestId}:${lineKey}` via subjectMutationKey. */
/** F-145 — who sent the line back, to which step, when (ISO) and why. */
/**
 * F-144 — the PIM record of an update request's product as the API captured it at intake
 * (`pim_snapshot`, DdPimRecord): read-only, every filled field in print groups. The SPA never
 * interprets the keys — a new PIM column is one more row.
 */
export interface PimRecordField {
  key: string
  label: string
  value: string
}

export interface PimRecordGroup {
  key: string
  label: string
  fields: PimRecordField[]
}

export interface PimRecord {
  capturedAt: string
  pimId: string | null
  sku: string | null
  groups: PimRecordGroup[]
}

/**
 * F-144 (slice 3a, team thread 2026-10-13) — "What's changing": Candace's six boxes, set at
 * Triage; the stored / wire list carries the MARKED items only (API DdChangeIntents). Diane's
 * rule: only these count as changes downstream.
 */
export type ChangeIntentKey = 'sku' | 'uin' | 'name' | 'build' | 'artwork' | 'price'

export interface ChangeIntent {
  key: ChangeIntentKey
  note: string
}

export interface SentBackState {
  stepKey: string
  reason: string
  /** NOTIF-005 — ours or the client's; absent on a line sent back before the cause existed. */
  cause?: SendBackCause | null
  by: string
  at: string
}

export interface SubjectMutation {
  deliverableUpdates?: Record<string, Partial<DeliverableEntry>>
  activityHead?: ActivityEntry[]
  deliverableOpenedAt?: Record<string, number>
  deliverableAssigneeLogs?: Record<string, AssigneeLogEntry[]>
  deliverableCompletedAt?: Record<string, number>
  estimationMode?: EstimationMode
  pimMode?: PimMode
  installRequired?: boolean
  attributes?: Record<string, string>
  /** DD-ATTR-PRECEDENCE-001 — per-key intake|human; absent key = unknown/gap-fillable. */
  attributeSources?: Record<string, 'intake' | 'human'>
  /** F-099 — the line's parts list (API `line_parts`); absent = none stored. */
  parts?: LinePart[]
  /** F-144 — the product's PIM record captured at intake (update requests); absent = none. */
  pimRecord?: PimRecord
  /** F-144 (slice 3a) — "What's changing" as Triage saved it; [] = nothing changes; absent = never set. */
  changeIntents?: ChangeIntent[]
  /** F-099 — who wrote the parts last (`line_parts_source`); set even when the list was cleared. */
  partsSource?: 'intake' | 'human'
  chat?: ChatMessage[]
  chatReplies?: Record<string, ChatMessage[]>
  nameOverride?: string
  bundleReleased?: boolean
  deletedAt?: string
  deletedBy?: string
  deletedReason?: string
  revisionRound?: number
  designTimerMs?: number
  designTimerStartedAt?: number | null
  /** PR2b-3: per-step work timers; design UI reads `stepTimers.design` (also mirrored to designTimer*). */
  stepTimers?: Record<string, { ms: number; startedAt: number | null }>
  proofVersions?: ProofVersion[]
  quotedProofId?: string
  /** F-145 — the line was sent back to `stepKey` with a reason; cleared when that step is done again. */
  sentBack?: SentBackState
  pmsColors?: string[]
  packetNotes?: string
  /** ROUTING-E1 — PIQ quote commercial fields (line SoT). */
  quoteNumber?: string | null
  unitPriceCents?: number | null
  taxRate?: number | null
  paymentTerms?: string | null
  quoteUrl?: string | null
  /** HS-DEAL-ATTACH-PARSE-002 — parser status / ownership. */
  piqQuoteStatus?: PiqQuoteStatus | null
  piqUnitPriceSource?: PiqUnitPriceSource | null
  /** ROUTING-E3 — HubSpot ticket/deal record-only breadcrumbs (line SoT). */
  hubspotTicketId?: string
  hubspotTicketStage?: string
  /** HS-SYNC-B-001 — resolved label when known (display). */
  hubspotTicketStageLabel?: string
  hubspotDealId?: string
  hubspotDealStage?: string
  /** Active kind=quote file (replace-in-place, no versioning). */
  quoteFile?: DesignFile | null
  filesByCategory?: Record<string, DesignFile[]>
  /** DD-BE-PR2b-1: optimistic locking — line header version from server snapshot. */
  workflowLineVersion?: number
  /** DD-BE-PR2b-1: per-step versions keyed by step_key. */
  workflowStepVersions?: Record<string, number>
  /** DD-LINE-ID-001 — persisted ordinal from server snapshot. */
  lineOrdinal?: number
  /** DD-LINE-ID-001 — server display id (do not recompute from filtered index). */
  lineDisplayId?: string
  /** DD-LINE-ID-CLEANUP-001 — persisted line_key from server snapshot. */
  lineKey?: string
  /** DD-DUE-24H-002 — line.due_at from queue-state (SoT once line exists). */
  dueAtIso?: string
}

/** HS-PARSE-CR3 — closed set of parser status / price-source values. */
export type PiqQuoteStatus =
  | 'ok'
  | 'needs_review'
  | 'unrecognized_template'
  | 'parse_failed'

export type PiqUnitPriceSource = 'parser' | 'human'

const PIQ_QUOTE_STATUSES: ReadonlySet<string> = new Set([
  'ok',
  'needs_review',
  'unrecognized_template',
  'parse_failed',
])

const PIQ_UNIT_PRICE_SOURCES: ReadonlySet<string> = new Set(['parser', 'human'])

export function narrowPiqQuoteStatus(value: unknown): PiqQuoteStatus | null {
  return typeof value === 'string' && PIQ_QUOTE_STATUSES.has(value)
    ? (value as PiqQuoteStatus)
    : null
}

export function narrowPiqUnitPriceSource(value: unknown): PiqUnitPriceSource | null {
  return typeof value === 'string' && PIQ_UNIT_PRICE_SOURCES.has(value)
    ? (value as PiqUnitPriceSource)
    : null
}
